import { STATUS } from "@/constants/statuses";
import { REWARDS_DISCOUNT_TYPES } from "@apps/rewards/constants/rewardsDiscountTypes";
import { getLoadingStatus } from "@apps/rewards/utils/statuses";
import { applyTransaction } from "@datorama/akita";
import PopupManager from "@lib/PopupManager";
import { TokenManagerModule } from "@modules/token_manager/TokenManagerModule";
import type { Observable } from "rxjs";
import { catchError, combineLatest, filter, map, tap } from "rxjs";
import { inject, singleton } from "tsyringe";

import type { LoggerInterface } from "@interfaces/LoggerInterface";

import { REFERRAL_SHARE_PATH } from "../../../constants/constants";
import { REWARDS_REWARD_TYPES } from "../../../constants/rewardsRewardTypes";
import { rewardsApplicationLoggerToken } from "../../../tokens";
import { getShareUrl } from "../../../utils/getShareUrl";
import type { RewardsProgramAccountEntity } from "../../ProgramAccount/models/RewardsProgramAccountEntity";
import type { RewardsStorefrontValuesSettingsEntity } from "../../StorefrontValues/models/RewardsStorefrontValuesEntities";
import { RewardsReferralEmailShareEntity } from "../../StorefrontValues/types";
import RewardsReferralProgramApiService from "../api_services/RewardsReferralProgramApiService";
import { REWARDS_REFERRAL_SHARE_CHANNELS } from "../constants";
import type {
    RewardsReferralProgramEntity,
    RewardsReferralProgramRewardEntity,
} from "../models/RewardsReferralProgramEntities";
import RewardsReferralProgramStore from "../stores/RewardsReferralProgramStore";
import type { REWARDS_REFERRAL_SHARE_CHANNELS_TYPE } from "../types";

@singleton()
class RewardsReferralProgramService {
    constructor(
        @inject(rewardsApplicationLoggerToken)
        private readonly logger: LoggerInterface,
        private readonly referralProgramApiService: RewardsReferralProgramApiService,
        private readonly referralProgramStore: RewardsReferralProgramStore,
        private readonly tokenManagerModule: TokenManagerModule
    ) {}

    public handleOpenReferralShareWindow = (
        channelType: REWARDS_REFERRAL_SHARE_CHANNELS_TYPE
    ) => {
        const url = getShareUrl(
            REFERRAL_SHARE_PATH,
            { channelType },
            this.tokenManagerModule.exports.tokenManager
        );

        const popupManager = new PopupManager(url);
        popupManager.open();
    };

    private getReward = (
        reward: RewardsReferralProgramRewardEntity,
        currencySymbol: string
    ) => {
        if (!reward) return "";

        let result = "";

        switch (reward.type) {
            case REWARDS_REWARD_TYPES.giftCard:
                result = `${currencySymbol}${reward.initialValue} gift card`;
                break;

            case REWARDS_REWARD_TYPES.discount:
                if (reward.discountType === REWARDS_DISCOUNT_TYPES.fixedAmount)
                    result = `${currencySymbol}${Math.abs(
                        reward.discountValue ?? 0
                    )} off discount`;

                if (reward.discountType === REWARDS_DISCOUNT_TYPES.percentage)
                    result = `${Math.abs(
                        reward.discountValue ?? 0
                    )}% off discount`;

                if (reward.discountType === REWARDS_DISCOUNT_TYPES.freeShipping)
                    result = "free shipping";

                break;

            case REWARDS_REWARD_TYPES.freeProduct:
                result = `${Math.abs(reward.discountValue ?? 0)}% discount on ${
                    reward.productTitle
                }`;
                break;
        }

        return result;
    };

    private replaceEmailPlaceholders(
        text: string,
        replacements: { [key: string]: string }
    ): string {
        return text.replace(
            /{{(.*?)}}/g,
            (_, key: string) => replacements[key.trim()]
        );
    }

    public selectMailToLink = ({
        shopName,
        currencySymbol,
        referralData$,
        referralShareChannels$,
        receiverReward$,
    }: {
        shopName: string;
        currencySymbol: string;
        referralData$: Observable<RewardsProgramAccountEntity["referral"]>;
        referralShareChannels$: Observable<
            RewardsStorefrontValuesSettingsEntity["referralShareChannels"]
        >;
        receiverReward$: Observable<RewardsReferralProgramRewardEntity | null>;
    }) => {
        return combineLatest([
            referralShareChannels$,
            referralData$,
            receiverReward$,
        ]).pipe(
            map(([referralShareChannels, referralData, receiverReward]) => [
                referralShareChannels.find(
                    (channel): channel is RewardsReferralEmailShareEntity =>
                        channel.channelType ===
                            REWARDS_REFERRAL_SHARE_CHANNELS.emailShare &&
                        channel.isEnabled
                ),
                referralData,
                receiverReward,
            ]),
            filter(
                (
                    value
                ): value is [
                    RewardsReferralEmailShareEntity,
                    RewardsProgramAccountEntity["referral"],
                    RewardsReferralProgramRewardEntity
                ] => !!value.length
            ),
            map(([emailShareChannel, referralData, receiverReward]) => {
                if (!emailShareChannel) return "";

                const subject = this.replaceEmailPlaceholders(
                    emailShareChannel.title,
                    { shop_name: shopName }
                );

                const body = this.replaceEmailPlaceholders(
                    emailShareChannel.message,
                    {
                        shop_name: shopName,
                        link: referralData?.referralLink ?? "",
                        reward_type: this.getReward(
                            receiverReward,
                            currencySymbol
                        ),
                    }
                );

                return `mailto:?subject=${subject}&body=${body}`;
            })
        );
    };

    public getReferralProgram =
        (): Observable<RewardsReferralProgramEntity> => {
            this.logger.debug(
                "RewardsReferralProgramService.getReferralProgram is started"
            );

            const status = this.referralProgramStore.getStatus();
            this.referralProgramStore.setStatus(getLoadingStatus(status));

            return this.referralProgramApiService
                .getReferralProgramReward()
                .pipe(
                    catchError((error: unknown) => {
                        this.logger.debug(
                            "RewardsReferralProgramService.getReferralProgram is errored",
                            error
                        );
                        this.referralProgramStore.setStatus(STATUS.ERROR);
                        throw error;
                    }),
                    tap((value) => {
                        this.logger.debug(
                            "RewardsReferralProgramService.getReferralProgram is ready"
                        );
                        //Store Update
                        applyTransaction(() => {
                            this.referralProgramStore.update((state) => ({
                                ...state,
                                referralProgram: value,
                            }));
                            this.referralProgramStore.setStatus(STATUS.READY);
                        });
                    })
                );
        };
}

export default RewardsReferralProgramService;
